import {Component, EventEmitter, Input, Output} from '@angular/core';
import {AcabimProject, BimDataModelPhase, BimModelService, getAllEnumValues, ProjectApiService} from '@cas-hub/acabim-common-client';
import {MessageService} from 'primeng/api';
import {NavigatorResult} from '@cas-hub/acabim-qa-client';
import {FormGroup} from '@angular/forms';

export type BimPhaseInfo = { name: string, phase: BimDataModelPhase };

@Component({
    selector: 'acabim-admin-transfer-project-phase',
    templateUrl: './transfer-project-phase.component.html',
    styleUrls: ['./transfer-project-phase.component.scss']
})
export class TransferProjectPhaseComponent {

    @Output() projectPhaseTransferred = new EventEmitter<BimDataModelPhase>();

    @Input() editForm: FormGroup | null = null;

    @Input()
    public set project(val: AcabimProject | null) {
        this._project = null; // The value will be set later after ensuring the project has phase info
        this.showProceed = false;
        this.ensureProjectHasPhaseInfo(val)
            .then(pr => this._project = pr)
            .then(_ => this.refreshSupportedPhaseTransfers());
    }

    public get project(): AcabimProject | null {
        return this._project;
    }

    public supportedPhaseTransfers: BimPhaseInfo[] = [];
    public selectedPhaseTransfer?: BimPhaseInfo;
    public currentQaCapName?: string; // Holds the QA CAP name that has to be displayed. Set in `onSelectedPhaseChanged` event handler
    public showProceed = false; // Boolean value which determines if the `proceed` button is visible

    public yearRange: string; // The range for the model phase date
    public dateLabel?: string; // If a date label is set, then we assume that a date is required to go along with the bim model transfer (ConstructionStartDate etc)
    public selectedDate?: Date;

    public isLoading = false;

    private _project: AcabimProject | null = null;

    constructor(private readonly projectApiService: ProjectApiService,
                private readonly bimModelService: BimModelService,
                private readonly messageService: MessageService) {
        this.yearRange = ((new Date()).getFullYear() - 100) + ':' + ((new Date()).getFullYear() + 10);
    }

    /**
     * Called when the user has selected a different phase to transfer to
     */
    public onSelectedPhaseChanged(val: BimPhaseInfo): void {
        this.selectedPhaseTransfer = val;
        this.showProceed = false;

        if (this.selectedPhaseTransfer) {
            if (this.project?.currentPhase) {
                this.currentQaCapName = this.bimModelService.getTransferBimModelPhaseQaCapName(BimDataModelPhase[this.project.currentPhase], this.selectedPhaseTransfer.phase);
            }

            switch (this.selectedPhaseTransfer.phase) {
                case BimDataModelPhase.Construction:
                    this.dateLabel = 'Construction Start Date';
                    break;

                case BimDataModelPhase.PostConstruction:
                    this.dateLabel = 'Practical Completion Date';
                    break;

                default:
                    this.dateLabel = undefined;
                    break;
            }
        }
    }

    public onCapResult(val: NavigatorResult | undefined) {
        this.showProceed = val?.detectedResult ?? false;
    }

    public transferPhase(): void {
        if (this.selectedPhaseTransfer && this.project) {
            if (this.dateLabel && !this.editForm?.get('constructionDate')?.value) {
                this.messageService.add({detail: `${this.dateLabel} is required to move project ${this.project.name} to ${this.selectedPhaseTransfer.name}`, severity: 'error'});
                return;
            }

            this.showProceed = false;
            this.currentQaCapName = undefined;
            const project = this.project;
            this.isLoading = true;
            this.bimModelService.transferModelPhase(this.selectedPhaseTransfer.phase, project, undefined, this.editForm?.get('constructionDate')?.value ?? new Date()).subscribe(
                data => {
                    this.messageService.add({detail: `Project ${project.name} transferred to ${this.selectedPhaseTransfer?.name}`});
                    this.selectedDate = undefined;
                    this.isLoading = false;
                    this.projectPhaseTransferred.emit(this.selectedPhaseTransfer?.phase);
                },
                error => {
                    console.dir(error);
                    if(error.status === 404) {
                        this.messageService.clear();
                        this.messageService.add({severity: 'error', summary: 'No model exists for this project', key: 'projectMessage'});
                    }
                    this.isLoading = false;
                }
            )
            // this.bimModelService.transferModelPhase(this.selectedPhaseTransfer.phase, project, undefined, this.editForm?.get('constructionDate')?.value ?? new Date()).toPromise()
            //     .then(_ => {
            //         this.messageService.add({detail: `Project ${project.name} transferred to ${this.selectedPhaseTransfer?.name}`});
            //         this.selectedDate = undefined;
            //         this.isLoading = false;
            //         this.projectPhaseTransferred.emit(this.selectedPhaseTransfer?.phase);
            //     });
        }
    }

    private async ensureProjectHasPhaseInfo(project: AcabimProject | null): Promise<AcabimProject | null> {
        // Sometimes the passed in project might not have its phase information - mainly because it was not requested from the server.
        // So if not, then we'll request that information here, if it does then we don't need another server call.

        if (project) {
            if (project.currentPhase) {
                return project;
            }

            return this.getProject(project.id);
        }

        return null;
    }

    private getProject(projectId: string): Promise<AcabimProject> {
        return this.projectApiService.getProject(projectId, {includePhase: true}).toPromise();
    }

    private refreshSupportedPhaseTransfers(): void {
        this.supportedPhaseTransfers = [];

        if (this.project) {
            const currentPhase = BimDataModelPhase[this.project.currentPhase];
            // We'll grab all the supported phases and retrieve all the phases to which the current phase of the project can be transferred to
            for (const modelPhase of getAllEnumValues(BimDataModelPhase)) {
                const checkPhase = Number(BimDataModelPhase[modelPhase as any]);
                if (this.bimModelService.canTransferBimModelPhase(currentPhase, checkPhase)) {
                    this.supportedPhaseTransfers.push({name: modelPhase, phase: checkPhase});
                }
            }

            if (this.supportedPhaseTransfers.length) {
                this.onSelectedPhaseChanged(this.supportedPhaseTransfers[0]);
            }
        }
    }
}
